/**
 * useUsdPricing (F-285) — loads the USD tier/service catalog from the
 * usd-catalog backend (F-284) with pure-fallback resilience: if the API is
 * unreachable (public landing, backend down, wrong stage), the mirror in
 * usdPricingModel.ts keeps the pricing page fully usable.
 */

import { useCallback, useEffect, useState } from 'react';
import { landlordApi } from '../services/api';
import {
  USD_FALLBACK_PLANS,
  USD_FALLBACK_SERVICES,
  UsdPlan,
  UsdService,
} from '../services/usdPricingModel';

interface CatalogState {
  plans: UsdPlan[];
  services: UsdService[];
  /** true when the live catalog loaded; false = fallback mirror in use */
  live: boolean;
  loading: boolean;
  error: string | null;
}

function unwrap(payload: any): any {
  return payload?.data?.data ?? payload?.data ?? payload;
}

export function useUsdPricing() {
  const [state, setState] = useState<CatalogState>({
    plans: USD_FALLBACK_PLANS,
    services: USD_FALLBACK_SERVICES,
    live: false,
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    try {
      const [plansRes, servicesRes] = await Promise.all([
        landlordApi.get('api/usd-catalog/plans'),
        landlordApi.get('api/usd-catalog/services'),
      ]);
      const plans = (unwrap(plansRes)?.plans || []).filter((p: any) => p && p.id);
      const services = (unwrap(servicesRes)?.services || []).filter((s: any) => s && s.type);
      setState({
        plans: plans.length ? plans : USD_FALLBACK_PLANS,
        services: services.length ? services : USD_FALLBACK_SERVICES,
        live: plans.length > 0,
        loading: false,
        error: null,
      });
    } catch (err: any) {
      // Fallbacks keep the page usable; surface a soft error for telemetry.
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'USD catalog unavailable — showing standard pricing.',
      }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return state;
}
